import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "formulario-modelo" }
const _hoisted_2 = {
  key: 0,
  class: "info"
}
const _hoisted_3 = {
  key: 1,
  class: "error"
}
const _hoisted_4 = { class: "modelo-nombre" }
const _hoisted_5 = { class: "fila" }
const _hoisted_6 = { class: "fila" }
const _hoisted_7 = ["disabled"]
const _hoisted_8 = { class: "fila" }
const _hoisted_9 = { class: "fila" }
const _hoisted_10 = { class: "fila" }
const _hoisted_11 = { class: "fila" }
const _hoisted_12 = { class: "fila" }
const _hoisted_13 = {
  key: 0,
  class: "edit-section"
}
const _hoisted_14 = { class: "form-group" }
const _hoisted_15 = ["disabled"]
const _hoisted_16 = { class: "form-group" }
const _hoisted_17 = ["disabled"]
const _hoisted_18 = { class: "form-group" }
const _hoisted_19 = { class: "rango-vigencia" }
const _hoisted_20 = ["disabled"]
const _hoisted_21 = ["disabled"]
const _hoisted_22 = { class: "form-group" }
const _hoisted_23 = ["disabled"]
const _hoisted_24 = { class: "form-group" }
const _hoisted_25 = { class: "escala-toggle" }
const _hoisted_26 = ["disabled"]
const _hoisted_27 = ["disabled"]
const _hoisted_28 = { class: "form-group" }
const _hoisted_29 = ["disabled"]
const _hoisted_30 = {
  key: 0,
  class: "niveles-listado"
}
const _hoisted_31 = ["for"]
const _hoisted_32 = ["id", "onUpdate:modelValue", "disabled"]
const _hoisted_33 = { class: "botones" }
const _hoisted_34 = ["disabled"]
const _hoisted_35 = ["disabled"]
const _hoisted_36 = ["disabled"]
const _hoisted_37 = ["disabled"]
const _hoisted_38 = { key: 3 }

import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import useModelo from '@/store/GestionModelo'
import type EstructuraModelo from '@/interfaces/EstructuraModelo'
import MensajeFlotante from './MensajeFlotante.vue'
import { limpiarNiveles, formatearNivelesParaMostrar } from '@/utils/nivelesHelper'


export default /*@__PURE__*/_defineComponent({
  __name: 'MostrarEstructura',
  setup(__props) {

/**
 * Árbol completo del modelo en modo consulta.
 *
 * ── Enrutado ─────────────────────────────────────────────────────
 *   `Estructura/:id_modelo/:id_version?` (EstructuraModelo) — permiso `modelos.ver`
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

const route = useRoute()
const router = useRouter()
const modeloStore = useModelo()
const modelo = ref<EstructuraModelo | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)
const nombreEdit = ref('')
const cantidadNiveles = ref(0)
const niveles = ref<string[]>([])
const idModelo = Number(route.params.id_modelo ?? NaN)
const nivelesString = computed(() => formatearNivelesParaMostrar(niveles.value))
const editMode = ref(false)
const saving = ref(false)
const mensajeVisible = ref(false)
const mensajeTexto = ref('')
const mensajeTipo = ref<'positivo' | 'error'>('positivo')
const fechaEdit = ref('')
const escalaMaxEdit = ref<5 | 10>(5)
// Vigencia normativa (rango de años) y acuerdo: independientes de fecha_creacion.
const vigenciaInicioEdit = ref<number | null>(null)
const vigenciaFinEdit = ref<number | null>(null)
const acuerdoEdit = ref('')

const vigenciaTexto = computed(() =>
  vigenciaInicioEdit.value != null && vigenciaFinEdit.value != null
    ? `${vigenciaInicioEdit.value}-${vigenciaFinEdit.value}`
    : '-'
)

// Mismas reglas que el backend. Devuelve el mensaje de error, o null si es válido.
function validarVigencia(): string | null {
  const inicio = vigenciaInicioEdit.value
  const fin = vigenciaFinEdit.value

  if (inicio == null && fin == null) return null
  if (inicio == null || fin == null) {
    return 'La vigencia requiere el año inicial y el año final.'
  }
  if (!Number.isInteger(inicio) || !Number.isInteger(fin)) {
    return 'Los años de vigencia deben ser números enteros.'
  }
  if (inicio < 1900 || inicio > 2200 || fin < 1900 || fin > 2200) {
    return 'Los años de vigencia deben estar entre 1900 y 2200.'
  }
  if (fin < inicio) {
    return 'El año final de la vigencia no puede ser anterior al año inicial.'
  }
  return null
}

function mostrarMensaje(texto: string, tipo: 'positivo' | 'error') {
  mensajeTexto.value = texto
  mensajeTipo.value = tipo
  mensajeVisible.value = true
}

const nivelesArray = computed(() => {
  if (!modelo.value?.niveles) return []

  if (Array.isArray(modelo.value.niveles)) {
    return modelo.value.niveles.map(n => n.trim()).filter(n => n !== '')
  }

  if (typeof modelo.value.niveles === 'string') {
    return modelo.value.niveles.split(',').map(n => n.trim()).filter(n => n !== '')
  }

  return []
})

function validarParametrosRuta(): boolean {
  if (!Number.isFinite(idModelo) || idModelo <= 0) {
    error.value = `Error en parámetros de ruta: id_modelo inválido: ${idModelo}`
    console.warn('[MostrarEstructura] Parámetros inválidos detectados:', error.value)
    return false
  }
  return true
}

async function cargar() {
  loading.value = true
  error.value = null
  try {
    const m = await modeloStore.getModeloById(idModelo)
    if (!m) throw new Error('No se encontró el modelo.')
    modelo.value = m
    nombreEdit.value = m.nombre
    fechaEdit.value = (m.fecha_creacion ?? '').substring(0, 10)
    escalaMaxEdit.value = m.escala_max ?? 5
    vigenciaInicioEdit.value = m.vigencia_inicio ?? null
    vigenciaFinEdit.value = m.vigencia_fin ?? null
    acuerdoEdit.value = m.acuerdo ?? ''
    const arrNiveles = limpiarNiveles(m.niveles)
    cantidadNiveles.value = arrNiveles.length
    niveles.value = arrNiveles
  } catch (err: any) {
    error.value = err?.message || 'Error desconocido al cargar el modelo.'
    modelo.value = null
  } finally {
    loading.value = false
  }
}

function volver() {
  router.push({ name: 'ListaVersiones' })
}

function irAgregarComponentes() {
  router.push({ name: 'ComponentesModelo', params: { id_modelo: idModelo } })
}

function estadoClass(estado?: string | null) {
  switch (estado) {
    case 'aprobado': return 'estado-aprobado'
    case 'descontinuado': return 'estado-descontinuado'
    case 'completo': return 'estado-completo'
    default: return 'estado-modificar'
  }
}

// Función para formatear fecha
function formatFecha(fecha?: string | null) {
  if (!fecha) return '-'

  if (/^\d{4}-\d{2}-\d{2}$/.test(fecha)) {
    const [yyyy, mm, dd] = fecha.split('-').map(Number)
    return new Date(yyyy, mm - 1, dd).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  if (/^\d{4}-\d{2}-\d{2}T/.test(fecha)) {
    const [datePart] = fecha.split('T')
    const [yyyy, mm, dd] = datePart.split('-').map(Number)

    return new Date(yyyy, mm - 1, dd).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const d = new Date(fecha)
  return isNaN(d.getTime())
    ? fecha
    : d.toLocaleDateString('es-CO', { year: 'numeric', month: 'long', day: 'numeric' })
}

function toggleEditarGuardar() {
  if (!editMode.value) {
    editMode.value = true
    return
  }
  guardarCambios()
}

function irAGestionPesos() {
  if (!Number.isFinite(idModelo) || idModelo <= 0) {
    error.value = 'ID de modelo inválido.'
    return
  }
  router.push({ name: 'Pesos', params: { id_modelo: idModelo } })
}

function generarNiveles() {
  const nuevaCantidad = cantidadNiveles.value
  if (nuevaCantidad < 1) {
    niveles.value = []
    return
  }
  const nuevos = Array.from({ length: nuevaCantidad }, (_, i) => niveles.value[i] || '')
  niveles.value = nuevos
}

async function guardarCambios() {
  if (!modelo.value) return
  if (niveles.value.some(n => !n.trim())) {
    mostrarMensaje('Debe ingresar nombre para todos los niveles.', 'error')
    return
  }

  const errorVigencia = validarVigencia()
  if (errorVigencia) {
    mostrarMensaje(errorVigencia, 'error')
    return
  }

  saving.value = true
  try {
    // Actualizamos nombre, niveles, vigencia y acuerdo del modelo
    const modeloActualizado: EstructuraModelo = {
      ...modelo.value,
      nombre: nombreEdit.value.trim(),
      fecha_creacion: fechaEdit.value,
      escala_max: escalaMaxEdit.value,
      vigencia_inicio: vigenciaInicioEdit.value ?? null,
      vigencia_fin: vigenciaFinEdit.value ?? null,
      acuerdo: acuerdoEdit.value.trim() || null,
      niveles: niveles.value
    }
    const okModelo = await modeloStore.updateModelo(modeloActualizado)

    if (okModelo) {
      modelo.value = modeloActualizado
      mostrarMensaje('Cambios guardados correctamente.', 'positivo')
      editMode.value = false
    } else {
      mostrarMensaje('No se pudieron guardar los cambios.', 'error')
    }
  } catch (e: any) {
    mostrarMensaje('Error al guardar los cambios. Intente de nuevo.', 'error')
  } finally {
    saving.value = false
  }
}

function cancelarEdicion() {
  editMode.value = false
  if (modelo.value) {
    nombreEdit.value = modelo.value.nombre
    fechaEdit.value = (modelo.value.fecha_creacion ?? '').substring(0, 10)
    escalaMaxEdit.value = modelo.value.escala_max ?? 5
    vigenciaInicioEdit.value = modelo.value.vigencia_inicio ?? null
    vigenciaFinEdit.value = modelo.value.vigencia_fin ?? null
    acuerdoEdit.value = modelo.value.acuerdo ?? ''
    const arrNiveles = limpiarNiveles(modelo.value.niveles)
    cantidadNiveles.value = arrNiveles.length
    niveles.value = arrNiveles
  }
}

onMounted(() => {
  if (!validarParametrosRuta()) return
  cargar()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[32] || (_cache[32] = _createElementVNode("h1", null, "Estructura del Modelo", -1)),
    _cache[33] || (_cache[33] = _createElementVNode("div", { class: "divider" }, null, -1)),
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, "Cargando modelo..."))
      : (error.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createElementVNode("p", null, _toDisplayString(error.value), 1),
            _createElementVNode("button", {
              class: "btn guardar",
              onClick: cargar
            }, "Reintentar")
          ]))
        : (modelo.value)
          ? (_openBlock(), _createElementBlock("form", {
              key: 2,
              class: "modelo-form",
              onSubmit: _cache[10] || (_cache[10] = _withModifiers(() => {}, ["prevent"]))
            }, [
              _createElementVNode("h2", _hoisted_4, _toDisplayString(modelo.value.nombre), 1),
              _createElementVNode("div", _hoisted_5, [
                _cache[12] || (_cache[12] = _createElementVNode("label", null, "Estado", -1)),
                _createElementVNode("span", {
                  class: _normalizeClass(['estado', estadoClass(modelo.value.estado)])
                }, _toDisplayString(modelo.value.estado), 3)
              ]),
              _createElementVNode("div", _hoisted_6, [
                _cache[13] || (_cache[13] = _createElementVNode("label", null, "Nombre del Modelo", -1)),
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((nombreEdit).value = $event)),
                  type: "text",
                  class: _normalizeClass(["form-control", { editable: editMode.value }]),
                  disabled: !editMode.value || saving.value || modelo.value.estado !== 'edicion'
                }, null, 10, _hoisted_7), [
                  [_vModelText, nombreEdit.value]
                ])
              ]),
              _createElementVNode("div", _hoisted_8, [
                _cache[14] || (_cache[14] = _createElementVNode("label", null, "Niveles", -1)),
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((nivelesString).value = $event)),
                  type: "text",
                  class: "form-control",
                  disabled: ""
                }, null, 512), [
                  [_vModelText, nivelesString.value]
                ])
              ]),
              _createElementVNode("div", _hoisted_9, [
                _cache[15] || (_cache[15] = _createElementVNode("label", null, "Fecha de creación", -1)),
                _createElementVNode("output", null, _toDisplayString(formatFecha(modelo.value.fecha_creacion)), 1)
              ]),
              _createElementVNode("div", _hoisted_10, [
                _cache[16] || (_cache[16] = _createElementVNode("label", null, "Vigencia", -1)),
                _createElementVNode("output", null, _toDisplayString(vigenciaTexto.value), 1)
              ]),
              _createElementVNode("div", _hoisted_11, [
                _cache[17] || (_cache[17] = _createElementVNode("label", null, "Acuerdo", -1)),
                _createElementVNode("output", null, _toDisplayString(acuerdoEdit.value || '-'), 1)
              ]),
              _createElementVNode("div", _hoisted_12, [
                _cache[18] || (_cache[18] = _createElementVNode("label", null, "Escala de calificación", -1)),
                _createElementVNode("output", null, _toDisplayString(escalaMaxEdit.value === 10 ? '1 a 10' : '0 a 5'), 1)
              ]),
              (editMode.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                    _cache[28] || (_cache[28] = _createElementVNode("div", { class: "divider" }, null, -1)),
                    _cache[29] || (_cache[29] = _createElementVNode("h3", null, "Datos modificables", -1)),
                    _createElementVNode("div", _hoisted_14, [
                      _cache[19] || (_cache[19] = _createElementVNode("label", { for: "nombreEdit" }, "Nombre del modelo", -1)),
                      _withDirectives(_createElementVNode("input", {
                        id: "nombreEdit",
                        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((nombreEdit).value = $event)),
                        type: "text",
                        class: "form-control",
                        disabled: saving.value || modelo.value.estado !== 'edicion'
                      }, null, 8, _hoisted_15), [
                        [_vModelText, nombreEdit.value]
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_16, [
                      _cache[20] || (_cache[20] = _createElementVNode("label", { for: "fechaEdit" }, "Fecha de creación", -1)),
                      _withDirectives(_createElementVNode("input", {
                        id: "fechaEdit",
                        type: "date",
                        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((fechaEdit).value = $event)),
                        disabled: saving.value,
                        class: "fecha-edit-input"
                      }, null, 8, _hoisted_17), [
                        [_vModelText, fechaEdit.value]
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_18, [
                      _cache[22] || (_cache[22] = _createElementVNode("label", { for: "vigenciaInicio" }, "Vigencia del modelo", -1)),
                      _createElementVNode("div", _hoisted_19, [
                        _withDirectives(_createElementVNode("input", {
                          id: "vigenciaInicio",
                          type: "number",
                          min: "1900",
                          max: "2200",
                          step: "1",
                          placeholder: "2020",
                          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((vigenciaInicioEdit).value = $event)),
                          disabled: saving.value
                        }, null, 8, _hoisted_20), [
                          [
                            _vModelText,
                            vigenciaInicioEdit.value,
                            void 0,
                            { number: true }
                          ]
                        ]),
                        _cache[21] || (_cache[21] = _createElementVNode("span", { class: "rango-separador" }, "—", -1)),
                        _withDirectives(_createElementVNode("input", {
                          id: "vigenciaFin",
                          type: "number",
                          min: "1900",
                          max: "2200",
                          step: "1",
                          placeholder: "2025",
                          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((vigenciaFinEdit).value = $event)),
                          disabled: saving.value
                        }, null, 8, _hoisted_21), [
                          [
                            _vModelText,
                            vigenciaFinEdit.value,
                            void 0,
                            { number: true }
                          ]
                        ])
                      ]),
                      _cache[23] || (_cache[23] = _createElementVNode("small", { class: "ayuda" }, "Años de inicio y fin de vigencia (ej. 2020-2025).", -1))
                    ]),
                    _createElementVNode("div", _hoisted_22, [
                      _cache[24] || (_cache[24] = _createElementVNode("label", { for: "acuerdoEdit" }, "Acuerdo", -1)),
                      _withDirectives(_createElementVNode("input", {
                        id: "acuerdoEdit",
                        type: "text",
                        maxlength: "150",
                        placeholder: "Ejemplo: Acuerdo 02 de 2005",
                        "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((acuerdoEdit).value = $event)),
                        disabled: saving.value
                      }, null, 8, _hoisted_23), [
                        [_vModelText, acuerdoEdit.value]
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_24, [
                      _cache[25] || (_cache[25] = _createElementVNode("label", null, "Escala de calificación", -1)),
                      _createElementVNode("div", _hoisted_25, [
                        _createElementVNode("button", {
                          type: "button",
                          class: _normalizeClass(['escala-opcion', escalaMaxEdit.value === 5 ? 'activa' : '']),
                          disabled: saving.value,
                          onClick: _cache[7] || (_cache[7] = ($event: any) => (escalaMaxEdit.value = 5))
                        }, "0 a 5", 10, _hoisted_26),
                        _createElementVNode("button", {
                          type: "button",
                          class: _normalizeClass(['escala-opcion', escalaMaxEdit.value === 10 ? 'activa' : '']),
                          disabled: saving.value,
                          onClick: _cache[8] || (_cache[8] = ($event: any) => (escalaMaxEdit.value = 10))
                        }, "1 a 10", 10, _hoisted_27)
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_28, [
                      _cache[26] || (_cache[26] = _createElementVNode("label", { for: "cantidadNiveles" }, "Niveles", -1)),
                      _withDirectives(_createElementVNode("input", {
                        id: "cantidadNiveles",
                        type: "number",
                        min: "1",
                        placeholder: "Ejemplo: 3",
                        "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((cantidadNiveles).value = $event)),
                        onInput: generarNiveles,
                        disabled: saving.value
                      }, null, 40, _hoisted_29), [
                        [
                          _vModelText,
                          cantidadNiveles.value,
                          void 0,
                          { number: true }
                        ]
                      ])
                    ]),
                    _cache[30] || (_cache[30] = _createElementVNode("div", { class: "divider" }, null, -1)),
                    (niveles.value.length > 0)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_30, [
                          _cache[27] || (_cache[27] = _createElementVNode("h3", null, "Nombre los Niveles", -1)),
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(niveles.value, (nombre, index) => {
                            return (_openBlock(), _createElementBlock("div", {
                              key: index,
                              class: "form-group"
                            }, [
                              _createElementVNode("label", {
                                for: 'nivel-' + index
                              }, "Nivel " + _toDisplayString(index + 1), 9, _hoisted_31),
                              _withDirectives(_createElementVNode("input", {
                                id: 'nivel-' + index,
                                type: "text",
                                placeholder: "Nombre del Nivel",
                                "onUpdate:modelValue": ($event: any) => ((niveles.value[index]) = $event),
                                disabled: saving.value
                              }, null, 8, _hoisted_32), [
                                [_vModelText, niveles.value[index]]
                              ])
                            ]))
                          }), 128))
                        ]))
                      : _createCommentVNode("", true)
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_33, [
                (!editMode.value)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      _createElementVNode("button", {
                        type: "button",
                        class: "btn salir",
                        onClick: volver
                      }, "Salir"),
                      _createElementVNode("button", {
                        type: "button",
                        class: "btn guardar",
                        disabled: modelo.value.estado !== 'edicion',
                        onClick: irAgregarComponentes
                      }, " Agregar " + _toDisplayString(nivelesArray.value[0] ?? 'Elementos'), 9, _hoisted_34),
                      _createElementVNode("button", {
                        type: "button",
                        class: "btn guardar",
                        onClick: irAGestionPesos
                      }, " Ajustar Pesos "),
                      _createElementVNode("button", {
                        type: "button",
                        class: "btn guardar",
                        onClick: toggleEditarGuardar,
                        disabled: saving.value
                      }, " Modificar ", 8, _hoisted_35)
                    ], 64))
                  : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                      _createElementVNode("button", {
                        type: "button",
                        class: "btn salir",
                        onClick: cancelarEdicion,
                        disabled: saving.value
                      }, " Cancelar ", 8, _hoisted_36),
                      _createElementVNode("button", {
                        type: "button",
                        class: "btn guardar",
                        onClick: guardarCambios,
                        disabled: saving.value
                      }, _toDisplayString(saving.value ? 'Guardando...' : 'Guardar'), 9, _hoisted_37)
                    ], 64))
              ])
            ], 32))
          : (_openBlock(), _createElementBlock("div", _hoisted_38, [
              _cache[31] || (_cache[31] = _createElementVNode("p", null, "No fue posible cargar el Modelo.", -1)),
              _createElementVNode("div", { style: {"margin-top":"1rem"} }, [
                _createElementVNode("button", {
                  class: "btn guardar",
                  onClick: cargar
                }, "Reintentar")
              ])
            ])),
    _createVNode(MensajeFlotante, {
      visible: mensajeVisible.value,
      mensaje: mensajeTexto.value,
      tipo: mensajeTipo.value,
      onCerrar: _cache[11] || (_cache[11] = ($event: any) => (mensajeVisible.value = false))
    }, null, 8, ["visible", "mensaje", "tipo"])
  ]))
}
}

})